/**
 * Flow control nodes — branch, sequence, for loop, etc.
 */
import type { NodeBehaviour } from '../types';
import { flowIn, flowOut, input, output } from '../node/NodeRegistry';

export const Branch: NodeBehaviour = {
  id: 'flow.branch',
  name: 'Branch',
  category: 'Flow',
  color: '#f39c12',
  icon: 'GitBranch',
  description: 'Executes True or False path based on Condition.',
  keywords: ['if', 'condition', 'switch', 'toggle'],
  inputs: [
    flowIn(),
    input('condition', 'Condition', 'bool', false),
  ],
  outputs: [
    flowOut('true', 'True'),
    flowOut('false', 'False'),
  ],
  execute(ctx) {
    ctx.triggerFlow(ctx.getInput<boolean>('condition') ? 'true' : 'false');
  },
};

export const Sequence: NodeBehaviour = {
  id: 'flow.sequence',
  name: 'Sequence',
  category: 'Flow',
  color: '#f39c12',
  icon: 'List',
  description: 'Executes multiple outputs in order.',
  keywords: ['order', 'serial', 'chain'],
  inputs: [
    flowIn(),
  ],
  outputs: [
    flowOut('out_0', 'Then 0'),
    flowOut('out_1', 'Then 1'),
    flowOut('out_2', 'Then 2'),
    flowOut('out_3', 'Then 3'),
  ],
  execute(ctx) {
    for (let i = 0; i < 4; i++) {
      ctx.triggerFlow(`out_${i}`);
    }
  },
};

export const ForLoop: NodeBehaviour = {
  id: 'flow.forLoop',
  name: 'For Loop',
  category: 'Flow',
  color: '#f39c12',
  icon: 'Repeat',
  description: 'Loops from Start to End (exclusive).',
  keywords: ['iterate', 'repeat', 'count'],
  inputs: [
    flowIn(),
    input('start', 'Start', 'i32', 0),
    input('end', 'End', 'i32', 10),
  ],
  outputs: [
    flowOut('body', 'Loop Body'),
    flowOut('done', 'Completed'),
    output('index', 'Index', 'i32'),
  ],
};

export const WhileLoop: NodeBehaviour = {
  id: 'flow.whileLoop',
  name: 'While Loop',
  category: 'Flow',
  color: '#f39c12',
  icon: 'RotateCw',
  description: 'Repeats while condition is true.',
  keywords: ['iterate', 'repeat', 'condition'],
  inputs: [
    flowIn(),
    input('condition', 'Condition', 'bool', true),
  ],
  outputs: [
    flowOut('body', 'Loop Body'),
    flowOut('done', 'Completed'),
  ],
};

export const Delay: NodeBehaviour = {
  id: 'flow.delay',
  name: 'Delay',
  category: 'Flow',
  color: '#f39c12',
  icon: 'Clock',
  description: 'Waits for a specified duration (seconds).',
  keywords: ['wait', 'timer', 'sleep'],
  inputs: [
    flowIn(),
    input('duration', 'Seconds', 'f32', 1.0),
  ],
  outputs: [
    flowOut('done', 'Done'),
  ],
};

export const DoOnce: NodeBehaviour = {
  id: 'flow.doOnce',
  name: 'Do Once',
  category: 'Flow',
  color: '#f39c12',
  icon: 'ChevronsRight',
  description: 'Executes the output only once until reset.',
  keywords: ['single', 'once'],
  inputs: [
    flowIn(),
    flowIn('reset', 'Reset'),
  ],
  outputs: [
    flowOut(),
  ],
  execute(ctx) {
    if (ctx.triggeredPort === 'reset') {
      ctx.setState('done', false);
      return;
    }
    const done = ctx.getState<boolean>('done') ?? false;
    if (!done) {
      ctx.setState('done', true);
      ctx.triggerFlow('exec_out');
    }
  },
};

export const FlipFlop: NodeBehaviour = {
  id: 'flow.flipFlop',
  name: 'Flip Flop',
  category: 'Flow',
  color: '#f39c12',
  icon: 'ToggleRight',
  description: 'Alternates between two output paths.',
  keywords: ['toggle', 'alternate', 'switch'],
  inputs: [
    flowIn(),
  ],
  outputs: [
    flowOut('a', 'A'),
    flowOut('b', 'B'),
    output('isA', 'Is A', 'bool'),
  ],
  execute(ctx) {
    const isA = !(ctx.getState<boolean>('isA') ?? true);
    ctx.setState('isA', isA);
    ctx.setOutput('isA', isA);
    ctx.triggerFlow(isA ? 'a' : 'b');
  },
};

// ── Cache ───────────────────────────────────────────────────────

export const Cache: NodeBehaviour = {
  id: 'flow.cache',
  name: 'Cache',
  category: 'Flow',
  color: '#2ecc71',
  icon: 'Database',
  description: 'Stores a value on Set, outputs cached value on Get. Only re-evaluates when triggered.',
  keywords: ['store', 'save', 'remember', 'latch'],
  inputs: [
    flowIn('set', 'Set'),
    input('value', 'Value', 'any'),
  ],
  outputs: [
    flowOut('out', 'Out'),
    output('cached', 'Cached', 'any'),
  ],
  execute(ctx) {
    if (ctx.triggeredPort === 'set') {
      const val = ctx.getInput('value');
      ctx.setState('cached', val);
      ctx.setOutput('cached', val);
      ctx.triggerFlow('out');
    } else {
      // Reading cached value (from a data pull)
      const cached = ctx.getState('cached') ?? null;
      ctx.setOutput('cached', cached as any);
    }
  },
};

// ── Toggle Flow ─────────────────────────────────────────────────

export const ToggleFlow: NodeBehaviour = {
  id: 'flow.toggleFlow',
  name: 'Toggle Flow',
  category: 'Flow',
  color: '#f39c12',
  icon: 'ToggleLeft',
  description: 'Gates execution flow. Enable/Disable control which path opens/closes. When open, flow passes through.',
  keywords: ['gate', 'switch', 'enable', 'disable', 'block'],
  inputs: [
    flowIn('exec', 'Exec'),
    flowIn('enable', 'Enable'),
    flowIn('disable', 'Disable'),
  ],
  outputs: [
    flowOut('out', 'Out'),
    output('isOpen', 'Is Open', 'bool'),
  ],
  execute(ctx) {
    if (ctx.triggeredPort === 'enable') {
      ctx.setState('open', true);
      ctx.setOutput('isOpen', true);
      return;
    }
    if (ctx.triggeredPort === 'disable') {
      ctx.setState('open', false);
      ctx.setOutput('isOpen', false);
      return;
    }
    // Exec flow — pass through only if open
    const open = ctx.getState<boolean>('open') ?? true;
    ctx.setOutput('isOpen', open);
    if (open) ctx.triggerFlow('out');
  },
};

// ── Toggle Value ────────────────────────────────────────────────

export const ToggleValue: NodeBehaviour = {
  id: 'flow.toggleValue',
  name: 'Toggle Value',
  category: 'Flow',
  color: '#f39c12',
  icon: 'ToggleRight',
  description: 'Switches between A and B on each trigger. Starts with A.',
  keywords: ['alternate', 'switch', 'flip', 'ab'],
  inputs: [
    flowIn(),
    input('a', 'A', 'any'),
    input('b', 'B', 'any'),
  ],
  outputs: [
    flowOut(),
    output('result', 'Result', 'any'),
    output('isA', 'Is A', 'bool'),
  ],
  execute(ctx) {
    const isA = !(ctx.getState<boolean>('isA') ?? false);
    ctx.setState('isA', isA);
    const result = isA ? ctx.getInput('a') : ctx.getInput('b');
    ctx.setOutput('result', result);
    ctx.setOutput('isA', isA);
    ctx.triggerFlow('exec_out');
  },
};

// ── Formula ─────────────────────────────────────────────────────

export const Formula: NodeBehaviour = {
  id: 'flow.formula',
  name: 'Formula',
  category: 'Math',
  isPure: true,
  color: '#e67e22',
  icon: 'FileCode',
  description: 'Evaluates a math formula using named inputs A..H. Use standard JS math syntax: +, -, *, /, **, Math.sin(), etc.',
  keywords: ['expression', 'eval', 'calculate', 'equation', 'custom', 'math'],
  inputs: [
    input('formula', 'Formula', 'string', 'A + B'),
    input('a', 'A', 'f32', 0),
    input('b', 'B', 'f32', 0),
    input('c', 'C', 'f32', 0),
    input('d', 'D', 'f32', 0),
    input('e', 'E', 'f32', 0),
    input('f', 'F', 'f32', 0),
    input('g', 'G', 'f32', 0),
    input('h', 'H', 'f32', 0),
  ],
  outputs: [output('result', 'Result', 'f32')],
  execute(ctx) {
    const formula = ctx.getInput<string>('formula') ?? 'A + B';
    const A = ctx.getInput<number>('a') ?? 0;
    const B = ctx.getInput<number>('b') ?? 0;
    const C = ctx.getInput<number>('c') ?? 0;
    const D = ctx.getInput<number>('d') ?? 0;
    const E = ctx.getInput<number>('e') ?? 0;
    const F = ctx.getInput<number>('f') ?? 0;
    const G = ctx.getInput<number>('g') ?? 0;
    const H = ctx.getInput<number>('h') ?? 0;
    try {
      // TODO: replace with proper expression parser or ShadowRealm for isolation
      const fn = new Function('A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'Math',
        `"use strict"; return (${formula});`
      );
      const result = fn(A, B, C, D, E, F, G, H, Math);
      ctx.setOutput('result', typeof result === 'number' ? result : 0);
    } catch {
      ctx.setOutput('result', 0);
    }
  },
};

export const FLOW_NODES: readonly NodeBehaviour[] = [
  Branch, Sequence, ForLoop, WhileLoop, Delay, DoOnce, FlipFlop,
  Cache, ToggleFlow, ToggleValue, Formula,
];
